// Conversão do Markdown simples das respostas do assistente em blocos para o PDF do relatório.
//
// Segurança: nada aqui interpreta HTML. Tags como <b> ou <script> seguem como texto literal e
// o PDF só recebe texto (jsPDF.text), então não há como o conteúdo da resposta virar marcação.
//
// Blocos produzidos:
//   { tipo: 'titulo', nivel: 1..3, trechos }
//   { tipo: 'paragrafo', trechos }
//   { tipo: 'lista', ordenada, inicio, itens: [{ nivel, ordenada, trechos }] }
//   { tipo: 'tabela', colunas: [texto], linhas: [[texto]] }   (tabela em Markdown, com pipes)
//   { tipo: 'codigo', texto }
//   { tipo: 'separador' }
// `trechos` é uma lista de { texto, negrito } (o negrito é a única ênfase mantida).

const RE_TITULO = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const RE_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const RE_SEPARADOR = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const RE_CERCA = /^\s{0,3}(```|~~~)/;
const RE_LINHA_TABELA = /^\s*\|.*\|\s*$/;
const RE_DIVISA_TABELA = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

/** Remove marcações inline que não viram estilo no PDF (links, código, itálico, escapes). */
const limparInline = (texto) =>
  String(texto)
    .replace(/!\[([^\]]*)\]\(([^)]*)\)/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, rotulo, url) => (rotulo === url ? rotulo : `${rotulo} (${url})`))
    .replace(/`([^`]*)`/g, '$1')
    .replace(/(^|[^*\w])\*(?!\s)([^*]+?)\*(?!\w)/g, '$1$2')
    .replace(/~~(.+?)~~/g, '$1');

const desfazerEscapes = (texto) => texto.replace(/\\([\\`*_{}[\]()#+\-.!|~>])/g, '$1');

/** Separa o texto em trechos normais e em negrito (`**x**` ou `__x__`). */
export const textoParaTrechos = (texto) => {
  const fonte = limparInline(texto);
  const trechos = [];
  const re = /(\*\*|__)(\S(?:[\s\S]*?\S)?)\1/g;
  let ultimo = 0;
  let achado = re.exec(fonte);

  const empurrar = (pedaco, negrito) => {
    const textoFinal = desfazerEscapes(pedaco);
    if (!textoFinal) return;
    const anterior = trechos[trechos.length - 1];
    if (anterior && anterior.negrito === negrito) anterior.texto += textoFinal;
    else trechos.push({ texto: textoFinal, negrito });
  };

  while (achado) {
    empurrar(fonte.slice(ultimo, achado.index), false);
    empurrar(achado[2], true);
    ultimo = achado.index + achado[0].length;
    achado = re.exec(fonte);
  }
  empurrar(fonte.slice(ultimo), false);
  return trechos;
};

const celulasDaLinha = (linha) => {
  let conteudo = linha.trim();
  if (conteudo.startsWith('|')) conteudo = conteudo.slice(1);
  if (conteudo.endsWith('|') && !conteudo.endsWith('\\|')) conteudo = conteudo.slice(0, -1);
  // Divide nos pipes não escapados (sem lookbehind, que navegadores antigos não entendem).
  return conteudo
    .replace(/\\\|/g, '\uE000')
    .split('|')
    .map((celula) => trechosParaTexto(textoParaTrechos(celula.trim().replace(/\uE000/g, '|'))));
};

/** Junta os trechos de volta em texto puro (para células de tabela e testes). */
export const trechosParaTexto = (trechos) => trechos.map((t) => t.texto).join('');

/**
 * Converte o Markdown simples em blocos. Aceita qualquer valor (null vira lista vazia) e nunca
 * lança erro: o que não for reconhecido vira parágrafo.
 */
export const markdownParaBlocos = (markdown) => {
  const linhas = String(markdown ?? '').replace(/\r\n?/g, '\n').split('\n');
  const blocos = [];
  let paragrafo = [];
  let lista = null;

  const fecharParagrafo = () => {
    if (paragrafo.length) {
      const trechos = textoParaTrechos(paragrafo.join(' ').replace(/\s+/g, ' ').trim());
      if (trechos.length) blocos.push({ tipo: 'paragrafo', trechos });
    }
    paragrafo = [];
  };
  const fecharLista = () => {
    if (lista) blocos.push(lista);
    lista = null;
  };
  const fecharTudo = () => {
    fecharParagrafo();
    fecharLista();
  };

  for (let i = 0; i < linhas.length; i += 1) {
    const linha = linhas[i];

    if (RE_CERCA.test(linha)) {
      fecharTudo();
      const cerca = linha.trim().slice(0, 3);
      const conteudo = [];
      i += 1;
      while (i < linhas.length && !linhas[i].trim().startsWith(cerca)) {
        conteudo.push(linhas[i]);
        i += 1;
      }
      blocos.push({ tipo: 'codigo', texto: conteudo.join('\n') });
      continue;
    }

    if (!linha.trim()) {
      fecharParagrafo();
      // Uma linha em branco dentro da lista não a encerra se o próximo item continuar a lista.
      const proxima = linhas.slice(i + 1).find((l) => l.trim());
      if (lista && !(proxima && RE_ITEM.test(proxima))) fecharLista();
      continue;
    }

    const titulo = linha.match(RE_TITULO);
    if (titulo) {
      fecharTudo();
      const trechos = textoParaTrechos(titulo[2]);
      if (trechos.length) blocos.push({ tipo: 'titulo', nivel: Math.min(titulo[1].length, 3), trechos });
      continue;
    }

    if (RE_SEPARADOR.test(linha) && !paragrafo.length) {
      fecharTudo();
      blocos.push({ tipo: 'separador' });
      continue;
    }

    if (RE_LINHA_TABELA.test(linha) && i + 1 < linhas.length && RE_DIVISA_TABELA.test(linhas[i + 1])) {
      fecharTudo();
      const colunas = celulasDaLinha(linha);
      const linhasTabela = [];
      i += 2;
      while (i < linhas.length && RE_LINHA_TABELA.test(linhas[i])) {
        const celulas = celulasDaLinha(linhas[i]);
        linhasTabela.push(colunas.map((_, indice) => celulas[indice] ?? ''));
        i += 1;
      }
      i -= 1;
      blocos.push({ tipo: 'tabela', colunas, linhas: linhasTabela });
      continue;
    }

    const item = linha.match(RE_ITEM);
    if (item) {
      fecharParagrafo();
      const ordenada = /\d/.test(item[2]);
      if (lista && lista.ordenada !== ordenada && item[1].length === 0) fecharLista();
      if (!lista) {
        lista = { tipo: 'lista', ordenada, inicio: ordenada ? parseInt(item[2], 10) : 1, itens: [] };
      }
      const nivel = Math.min(Math.floor(item[1].replace(/\t/g, '  ').length / 2), 3);
      lista.itens.push({ nivel, ordenada, trechos: textoParaTrechos(item[3].trim()) });
      continue;
    }

    // Linha recuada logo depois de um item: continuação do texto do item.
    if (lista && /^\s+\S/.test(linha) && !paragrafo.length) {
      const ultimo = lista.itens[lista.itens.length - 1];
      ultimo.trechos = textoParaTrechos(`${trechosParaTextoMarcado(ultimo.trechos)} ${linha.trim()}`);
      continue;
    }

    fecharLista();
    paragrafo.push(linha.trim().replace(/^>\s?/, ''));
  }

  fecharTudo();
  return blocos;
};

// Recompõe os trechos com a marcação de negrito, para poder emendar a continuação de um item.
const trechosParaTextoMarcado = (trechos) =>
  trechos.map((t) => (t.negrito ? `**${t.texto}**` : t.texto)).join('');

export default markdownParaBlocos;
